import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  ChevronLeft, ChevronRight, CalendarRange, CalendarDays,
  CalendarClock, Loader2, CreditCard,
} from 'lucide-react';
import { toast } from 'sonner';

function dateOnly(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
function fmt(d) { return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
function iso(d) { return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString(); }
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function startOfWeek(d) {
  const c = new Date(d);
  const day = c.getDay();
  c.setDate(c.getDate() - day);
  return dateOnly(c);
}

export default function BookingCalendar() {
  const navigate = useNavigate();
  const [view, setView] = useState('available'); // available | day | week | month
  const [dogs, setDogs] = useState([]);
  const [dogId, setDogId] = useState('');
  const [runType, setRunType] = useState('any');
  const [startDate, setStartDate] = useState(() => addDays(dateOnly(new Date()), 1));
  const [endDate, setEndDate] = useState(() => addDays(dateOnly(new Date()), 3));
  const [availability, setAvailability] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState('stripe');

  useEffect(() => {
    api.get('/dogs').then((r) => {
      setDogs(r.data);
      if (r.data[0]) setDogId(r.data[0].id);
    });
  }, []);

  const refresh = async () => {
    if (endDate <= startDate) { toast.error('End must be after start'); return; }
    setLoading(true); setSelectedRun(null);
    try {
      const { data } = await api.get('/availability', {
        params: { start: iso(startDate), end: iso(endDate), run_type: runType === 'any' ? undefined : runType },
      });
      setAvailability(data);
    } catch { toast.error('Could not load availability'); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [runType, startDate.getTime(), endDate.getTime()]);

  const nights = useMemo(() => Math.max(1, Math.round((endDate - startDate) / (1000 * 60 * 60 * 24))), [startDate, endDate]);
  const pricePerNight = (rt) => (rt === '5x10' ? 65 : 45);
  const total = useMemo(() => (selectedRun ? nights * pricePerNight(selectedRun.type) : 0), [selectedRun, nights]);

  // ---------- DAY view ----------
  const [dayAnchor, setDayAnchor] = useState(() => dateOnly(new Date()));
  const [dayGrid, setDayGrid] = useState(null);
  useEffect(() => {
    if (view !== 'day') return;
    api.get('/availability/grid', { params: { start: iso(dayAnchor), days: 1 } }).then((r) => setDayGrid(r.data));
  }, [dayAnchor, view]);

  // ---------- WEEK view ----------
  const [weekAnchor, setWeekAnchor] = useState(() => startOfWeek(new Date()));
  const [weekGrid, setWeekGrid] = useState(null);
  useEffect(() => {
    if (view !== 'week') return;
    api.get('/availability/grid', { params: { start: iso(weekAnchor), days: 7 } }).then((r) => setWeekGrid(r.data));
  }, [weekAnchor, view]);

  // ---------- MONTH view ----------
  const [monthAnchor, setMonthAnchor] = useState(() => dateOnly(new Date()));
  const [monthGrid, setMonthGrid] = useState(null);
  useEffect(() => {
    if (view !== 'month') return;
    const start = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
    api.get('/availability/grid', { params: { start: iso(start), days: 35 } }).then((r) => setMonthGrid(r.data));
  }, [monthAnchor, view]);

  const book = async () => {
    if (!dogId) { toast.error('Add a dog first'); navigate('/client/dogs'); return; }
    if (!selectedRun) { toast.error('Select a run'); return; }
    setLoading(true);
    try {
      const { data: booking } = await api.post('/bookings', {
        dog_id: dogId, run_id: selectedRun.id, start: iso(startDate), end: iso(endDate),
      });
      toast.success(`Booking created — redirecting to ${provider === 'stripe' ? 'Stripe' : 'PayPal'}…`);
      if (provider === 'stripe') {
        const { data: ck } = await api.post('/payments/stripe/checkout', {
          booking_id: booking.id, origin_url: window.location.origin,
        });
        window.location.href = ck.url;
      } else {
        const { data: pp } = await api.post('/payments/paypal/create-order', { booking_id: booking.id });
        const approve = pp.links?.find((l) => l.rel === 'approve');
        if (approve) { window.location.href = approve.href; }
        else { toast.error('No PayPal approval URL'); }
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Booking failed';
      toast.error(detail);
      if (detail.includes('PayPal not configured')) toast.message('Use Stripe for payment, or add PayPal credentials to backend .env');
    } finally { setLoading(false); }
  };

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto'>
      <div className='flex items-center justify-between mb-4 sm:mb-6 flex-wrap gap-2'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Boarding</div>
          <h1 className='font-display text-2xl sm:text-3xl font-bold text-[#0B1220] dark:text-slate-50 mt-1'>Book a Run</h1>
          <p className='text-slate-500 dark:text-slate-400 text-xs sm:text-sm mt-1'>
            Live availability across 15 runs (7 × 5x5 · 8 × 5x10). No double-bookings.
          </p>
        </div>
      </div>

      <Card className='p-3 sm:p-4 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 grid grid-cols-2 lg:grid-cols-4 gap-3 items-end'>
        <div>
          <Label className='text-xs'>Dog</Label>
          <select
            className='w-full h-10 rounded-md border border-slate-200 dark:border-slate-700 px-3 text-sm bg-white dark:bg-slate-800 dark:text-slate-100'
            value={dogId}
            onChange={(e) => setDogId(e.target.value)}
            data-testid='book-dog-select'
          >
            {dogs.length === 0 ? <option value=''>— No dogs (add one first)</option> : dogs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <Label className='text-xs'>Run size</Label>
          <select
            className='w-full h-10 rounded-md border border-slate-200 dark:border-slate-700 px-3 text-sm bg-white dark:bg-slate-800 dark:text-slate-100'
            value={runType}
            onChange={(e) => setRunType(e.target.value)}
            data-testid='book-size-select'
          >
            <option value='any'>Any</option>
            <option value='5x5'>5x5 ($45/night)</option>
            <option value='5x10'>5x10 ($65/night)</option>
          </select>
        </div>
        <div>
          <Label className='text-xs'>Check-in</Label>
          <Input type='date' value={startDate.toISOString().slice(0, 10)} onChange={(e) => setStartDate(new Date(e.target.value + 'T00:00'))} data-testid='book-start-input' className='dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100' />
        </div>
        <div>
          <Label className='text-xs'>Check-out</Label>
          <Input type='date' value={endDate.toISOString().slice(0, 10)} onChange={(e) => setEndDate(new Date(e.target.value + 'T00:00'))} data-testid='book-end-input' className='dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100' />
        </div>
      </Card>

      <Tabs value={view} onValueChange={setView} className='mt-5 sm:mt-6'>
        <TabsList className='bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 w-full sm:w-auto grid grid-cols-4 sm:inline-grid sm:grid-cols-4'>
          <TabsTrigger value='available' data-testid='tab-available' className='text-xs sm:text-sm'>
            <CalendarDays className='w-4 h-4 mr-1 sm:hidden' />
            <span className='hidden sm:inline'>Available</span>
            <span className='sm:hidden'>Avail</span>
          </TabsTrigger>
          <TabsTrigger value='day' data-testid='tab-day' className='text-xs sm:text-sm'>
            <CalendarClock className='w-4 h-4 mr-1 sm:hidden' />
            <span>Day</span>
          </TabsTrigger>
          <TabsTrigger value='week' data-testid='tab-week' className='text-xs sm:text-sm'>
            <CalendarRange className='w-4 h-4 mr-1 sm:hidden' />
            <span>Week</span>
          </TabsTrigger>
          <TabsTrigger value='month' data-testid='tab-month' className='text-xs sm:text-sm'>
            <CalendarDays className='w-4 h-4 mr-1 sm:hidden' />
            <span>Month</span>
          </TabsTrigger>
        </TabsList>

        {/* AVAILABLE ------------------------------------------------ */}
        <TabsContent value='available'>
          <Card className='p-4 sm:p-5 mt-3 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'>
            <div className='flex items-center justify-between mb-3 flex-wrap gap-2'>
              <div>
                <div className='font-display font-semibold text-base sm:text-lg text-slate-900 dark:text-slate-50'>
                  {loading ? 'Checking availability…' : `${availability?.available_count || 0} runs available`}
                </div>
                <div className='text-xs text-slate-500 dark:text-slate-400'>{fmt(startDate)} → {fmt(endDate)} · {nights} nights</div>
              </div>
              <Button variant='outline' size='sm' onClick={refresh} disabled={loading} data-testid='refresh-availability'>
                {loading ? <Loader2 className='w-4 h-4 animate-spin' /> : 'Refresh'}
              </Button>
            </div>
            {availability && (
              <div className='grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5' data-testid='available-runs-grid'>
                {availability.all_runs.map((r) => {
                  const isAvailable = !availability.occupied_run_ids.includes(r.id);
                  const isSelected = selectedRun?.id === r.id;
                  return (
                    <button
                      key={r.id}
                      disabled={!isAvailable}
                      onClick={() => setSelectedRun(r)}
                      data-testid={`run-tile-${r.name}`}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        isSelected
                          ? 'border-[#1B3A87] bg-[#1B3A87]/5 dark:bg-[#1B3A87]/30 ring-2 ring-[#1B3A87]/30'
                          : isAvailable
                            ? 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-[#1B3A87]/40'
                            : 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 opacity-60 cursor-not-allowed'
                      }`}
                    >
                      <div className='font-mono text-sm font-semibold text-slate-900 dark:text-slate-50'>{r.name}</div>
                      <div className='text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 mt-0.5'>
                        {r.type} · {r.size_sqft} sqft
                      </div>
                      <div className='text-xs mt-2 font-semibold' style={{ color: isAvailable ? '#167A5A' : '#B42318' }}>
                        {isAvailable ? `$${pricePerNight(r.type) * nights}` : 'Booked'}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* DAY -------------------------------------------------------- */}
        <TabsContent value='day'>
          <Card className='p-4 sm:p-5 mt-3 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'>
            <div className='flex items-center justify-between mb-3'>
              <div className='font-display font-semibold text-base sm:text-lg text-slate-900 dark:text-slate-50'>
                {dayAnchor.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
              </div>
              <div className='flex gap-1'>
                <Button size='sm' variant='outline' onClick={() => setDayAnchor(addDays(dayAnchor, -1))} data-testid='day-prev'>
                  <ChevronLeft className='w-4 h-4' />
                </Button>
                <Button size='sm' variant='outline' onClick={() => setDayAnchor(dateOnly(new Date()))} data-testid='day-today'>
                  Today
                </Button>
                <Button size='sm' variant='outline' onClick={() => setDayAnchor(addDays(dayAnchor, 1))} data-testid='day-next'>
                  <ChevronRight className='w-4 h-4' />
                </Button>
              </div>
            </div>
            {dayGrid ? (
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-2' data-testid='day-runs'>
                {dayGrid.runs.map((r) => {
                  const ds = dayGrid.dates[0];
                  const occupied = dayGrid.grid[r.id]?.[ds];
                  const isAvailable = !occupied;
                  return (
                    <button
                      key={r.id}
                      disabled={!isAvailable}
                      onClick={() => {
                        setStartDate(dayAnchor);
                        setEndDate(addDays(dayAnchor, 1));
                        setSelectedRun(r);
                        setView('available');
                      }}
                      className={`flex items-center justify-between p-3 rounded-lg border text-left transition-all ${
                        isAvailable
                          ? 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-[#1B3A87]/50'
                          : 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 opacity-60'
                      }`}
                      data-testid={`day-run-${r.name}`}
                    >
                      <div>
                        <div className='font-mono text-sm font-semibold text-slate-900 dark:text-slate-50'>{r.name}</div>
                        <div className='text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 mt-0.5'>{r.type}</div>
                      </div>
                      <div className='text-xs font-semibold' style={{ color: isAvailable ? '#167A5A' : '#B42318' }}>
                        {isAvailable ? 'Available' : 'Occupied'}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className='py-10 text-center text-slate-500'><Loader2 className='w-4 h-4 animate-spin inline mr-2' /> Loading…</div>
            )}
          </Card>
        </TabsContent>

        {/* WEEK ------------------------------------------------------- */}
        <TabsContent value='week'>
          <Card className='p-4 sm:p-5 mt-3 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'>
            <div className='flex items-center justify-between mb-3'>
              <div className='font-display font-semibold text-base sm:text-lg text-slate-900 dark:text-slate-50'>
                Week of {fmt(weekAnchor)}
              </div>
              <div className='flex gap-1'>
                <Button size='sm' variant='outline' onClick={() => setWeekAnchor(addDays(weekAnchor, -7))} data-testid='week-prev'>
                  <ChevronLeft className='w-4 h-4' />
                </Button>
                <Button size='sm' variant='outline' onClick={() => setWeekAnchor(startOfWeek(new Date()))} data-testid='week-this'>
                  This week
                </Button>
                <Button size='sm' variant='outline' onClick={() => setWeekAnchor(addDays(weekAnchor, 7))} data-testid='week-next'>
                  <ChevronRight className='w-4 h-4' />
                </Button>
              </div>
            </div>
            {weekGrid ? (
              <div className='grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2' data-testid='week-grid'>
                {weekGrid.dates.map((ds) => {
                  const date = new Date(ds);
                  const occupied = weekGrid.runs.reduce((acc, r) => acc + (weekGrid.grid[r.id][ds] ? 1 : 0), 0);
                  const total = weekGrid.runs.length;
                  const pct = Math.round((occupied / total) * 100);
                  const tone = pct >= 90 ? '#B42318' : pct >= 60 ? '#B45309' : '#167A5A';
                  return (
                    <button
                      key={ds}
                      className='p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-[#1B3A87] dark:hover:border-[#93C5FD] text-left'
                      onClick={() => {
                        setStartDate(date);
                        setEndDate(addDays(date, 1));
                        setView('available');
                      }}
                      data-testid={`week-cell-${ds}`}
                    >
                      <div className='text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400'>
                        {date.toLocaleDateString(undefined, { weekday: 'short' })}
                      </div>
                      <div className='text-lg font-display font-bold text-slate-900 dark:text-slate-50 mt-0.5'>{date.getDate()}</div>
                      <div className='text-[10px] text-slate-500 dark:text-slate-400 mt-1'>{occupied}/{total} occupied</div>
                      <div className='h-1.5 rounded-full mt-1.5 bg-slate-100 dark:bg-slate-700 overflow-hidden'>
                        <div className='h-full' style={{ background: tone, width: `${pct}%` }} />
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className='py-10 text-center text-slate-500'><Loader2 className='w-4 h-4 animate-spin inline mr-2' /> Loading…</div>
            )}
          </Card>
        </TabsContent>

        {/* MONTH ------------------------------------------------------ */}
        <TabsContent value='month'>
          <Card className='p-4 sm:p-5 mt-3 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'>
            <div className='flex items-center justify-between mb-3'>
              <div className='font-display font-semibold text-base sm:text-lg text-slate-900 dark:text-slate-50'>
                {monthAnchor.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
              </div>
              <div className='flex gap-1'>
                <Button size='sm' variant='outline' onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1))} data-testid='month-prev'>
                  <ChevronLeft className='w-4 h-4' />
                </Button>
                <Button size='sm' variant='outline' onClick={() => setMonthAnchor(dateOnly(new Date()))} data-testid='month-today'>
                  Today
                </Button>
                <Button size='sm' variant='outline' onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1))} data-testid='month-next'>
                  <ChevronRight className='w-4 h-4' />
                </Button>
              </div>
            </div>
            {monthGrid && (
              <div className='grid grid-cols-7 gap-1 sm:gap-1.5' data-testid='month-grid'>
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <div key={i} className='text-center text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400 py-1'>{d}</div>
                ))}
                {monthGrid.dates.slice(0, 35).map((ds) => {
                  const date = new Date(ds);
                  const occupied = monthGrid.runs.reduce((acc, r) => acc + (monthGrid.grid[r.id][ds] ? 1 : 0), 0);
                  const total = monthGrid.runs.length;
                  const pct = Math.round((occupied / total) * 100);
                  const tone = pct >= 90 ? '#B42318' : pct >= 60 ? '#B45309' : '#167A5A';
                  return (
                    <button
                      key={ds}
                      className='aspect-square min-h-[60px] rounded-md border border-slate-200 dark:border-slate-700 p-1 hover:border-[#1B3A87] dark:hover:border-[#93C5FD] text-left bg-white dark:bg-slate-800'
                      onClick={() => {
                        setStartDate(date);
                        const e = new Date(date); e.setDate(e.getDate() + 2);
                        setEndDate(e);
                        setView('available');
                      }}
                      data-testid={`month-cell-${ds}`}
                    >
                      <div className='text-xs font-semibold text-slate-900 dark:text-slate-50'>{date.getDate()}</div>
                      <div className='text-[9px] text-slate-500 dark:text-slate-400 mt-0.5'>{occupied}/{total}</div>
                      <div className='h-1 rounded-full mt-1 bg-slate-100 dark:bg-slate-700 overflow-hidden'>
                        <div className='h-full' style={{ background: tone, width: `${pct}%` }} />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {selectedRun && (
        <Card
          className='mt-6 p-4 sm:p-5 border border-[#1B3A87]/30 bg-[#1B3A87]/[0.03] dark:bg-[#1B3A87]/20 dark:border-[#1B3A87]/60 sticky bottom-16 lg:bottom-0 z-20 backdrop-blur'
          data-testid='booking-summary-card'
        >
          <div className='flex flex-wrap items-center gap-3 sm:gap-4 justify-between'>
            <div>
              <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Booking Summary</div>
              <div className='font-display text-base sm:text-lg font-bold mt-1 text-slate-900 dark:text-slate-50'>
                Run <span className='font-mono'>{selectedRun.name}</span> · {nights} nights
              </div>
              <div className='text-xs text-slate-600 dark:text-slate-300'>{fmt(startDate)} → {fmt(endDate)}</div>
            </div>
            <div className='text-right'>
              <div className='text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400'>Total</div>
              <div className='font-display font-bold text-2xl sm:text-3xl tabular-nums text-slate-900 dark:text-slate-50'>${total.toFixed(2)}</div>
            </div>
            <div className='flex flex-col gap-2 w-full sm:w-auto'>
              <div className='flex gap-2'>
                <button
                  onClick={() => setProvider('stripe')}
                  className={`flex-1 sm:flex-none px-3 py-2 rounded-md border text-sm font-semibold ${
                    provider === 'stripe' ? 'border-[#1B3A87] bg-[#1B3A87] text-white' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 dark:text-slate-100'
                  }`}
                  data-testid='provider-stripe'
                >
                  Stripe
                </button>
                <button
                  onClick={() => setProvider('paypal')}
                  className={`flex-1 sm:flex-none px-3 py-2 rounded-md border text-sm font-semibold ${
                    provider === 'paypal' ? 'border-[#1B3A87] bg-[#1B3A87] text-white' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 dark:text-slate-100'
                  }`}
                  data-testid='provider-paypal'
                >
                  PayPal
                </button>
              </div>
              <Button
                onClick={book}
                disabled={loading}
                className='bg-[#C8102E] hover:bg-[#A50D26] text-white h-11'
                data-testid='confirm-book-button'
              >
                {loading ? <Loader2 className='w-4 h-4 animate-spin mr-2' /> : <CreditCard className='w-4 h-4 mr-2' />} Pay & Confirm
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
